import { useEffect, useState } from 'react';
import { usePrivate } from '@/components/private';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  SelectField,
  SelectFieldProps,
  Typography,
} from '@/primitives';
import { useRouter } from 'next/navigation';
import { organisationSelectorMessages } from './messages';
import { storeSelectedOrganisation } from '@/utils/storage';
import { ERoute } from '@/configs/routes';

export interface OrganisationSelectorModalProps {
  open: boolean;
  onClose: () => void;
}

export function OrganisationSelectorModal({
  open,
  onClose,
}: OrganisationSelectorModalProps) {
  const {
    userOrganisations,
    selectedOrganisationId,
    setSelectedOrganisation,
    setSelectedOrganisationId,
    setShowSplashScreen,
  } = usePrivate();

  const [selectedOrgId, setSelectedOrgId] = useState(-1);
  const router = useRouter();

  const handleConfirm = () => {
    if (selectedOrgId === selectedOrganisationId) return;
    if (!setSelectedOrganisation) return;
    if (!setSelectedOrganisationId) return;
    if (!setShowSplashScreen) return;

    const org = userOrganisations!.find(
      (org) => org.organisationId === selectedOrgId,
    )!.organisation!;
    const orgName = org.name!;

    setSelectedOrganisation(org);
    setSelectedOrganisationId(selectedOrgId);
    setShowSplashScreen(true);
    storeSelectedOrganisation(selectedOrgId, orgName);

    onClose();
    router.push(`/${orgName}/registry/${ERoute.DASHBOARD}`);
  };

  const handleOrganisationChange: SelectFieldProps['onChange'] = (event) => {
    setSelectedOrgId(parseInt(event.target.value as string));
  };

  useEffect(() => {
    setSelectedOrgId(selectedOrganisationId!);
  }, [selectedOrganisationId]);

  // reset selectedOrgId to selectedOrganisationId when close the modal
  useEffect(() => {
    if (!open) {
      setSelectedOrgId(selectedOrganisationId!);
    }
  }, [open, selectedOrganisationId]);

  return (
    <Dialog
      open={open}
      sx={{
        '.MuiDialog-paper': { maxHeight: '60vh' },
        '.MuiDialogContent-root': { borderTop: 'unset' },
      }}
    >
      <DialogTitle component="div" sx={{ paddingBottom: '12px' }}>
        <Typography variant="headlinesmall" sx={{ paddingBottom: '16px' }}>
          {organisationSelectorMessages['organisationSelector.title']}
        </Typography>
        <Typography variant="bodymedium" sx={{ paddingBottom: '16px' }}>
          {organisationSelectorMessages['organisationSelector.description']}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <SelectField
          displayEmpty
          fullWidth
          label={
            organisationSelectorMessages['organisationSelector.dropdown.label']
          }
          labelId="select-org-id"
          loading={false}
          onChange={handleOrganisationChange}
          value={selectedOrgId !== null ? selectedOrgId : ''}
          variant="filled"
        >
          <MenuItem value="" disabled>
            {organisationSelectorMessages['organisationSelector.title']}
          </MenuItem>
          {userOrganisations?.map((org) => (
            <MenuItem
              key={org.organisationId}
              value={org.organisationId}
              selected={org.organisationId === selectedOrgId}
            >
              {org.organisation?.name} - {org.organisation?.description}
            </MenuItem>
          ))}
        </SelectField>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px',
        }}
      >
        <Button onClick={onClose}>
          {
            organisationSelectorMessages[
              'organisationSelector.button.cancel.label'
            ]
          }
        </Button>
        <Button
          disabled={selectedOrgId === null}
          variant="contained"
          onClick={handleConfirm}
        >
          {
            organisationSelectorMessages[
              'organisationSelector.button.confirm.label'
            ]
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
